/* ==========================================================================
   True Backdrops — Photo Booth Profit Pack product template
   --------------------------------------------------------------------------
   Handles: gallery swap, mix-and-match picker (writes cart line item
   properties), variant matching, sticky bar, and buy buttons that live in
   other sections.

   No dependencies. Safe to load more than once — each component tracks its
   own init flag, and everything re-binds on shopify:section:load so the
   theme editor stays live.
   ========================================================================== */

(function () {
  'use strict';

  var INIT = 'ppBound';

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function bindOnce(el, fn) {
    if (!el || el.dataset[INIT] === 'true') return false;
    el.dataset[INIT] = 'true';
    fn(el);
    return true;
  }

  /* ------------------------------------------------------------------ *
   * Gallery
   * ------------------------------------------------------------------ */

  function initGallery(root) {
    var main = root.querySelector('[data-pp-main-image]');
    var thumbs = root.querySelectorAll('[data-pp-thumb]');
    if (!main || !thumbs.length) return;

    function select(btn) {
      each(thumbs, function (t) {
        t.setAttribute('aria-current', String(t === btn));
      });
      var img = btn.querySelector('img');
      if (!img) return;

      var full = btn.getAttribute('data-pp-full') || img.currentSrc || img.src;
      main.src = full;
      main.srcset = btn.getAttribute('data-pp-full-srcset') || '';
      main.alt = img.alt || '';
    }

    each(thumbs, function (btn) {
      btn.addEventListener('click', function () {
        select(btn);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Buy box — picker, variants, cart
   * ------------------------------------------------------------------ */

  function initBuyBox(root) {
    var form = root.querySelector('[data-pp-form]');
    var addBtn = root.querySelector('[data-pp-add]');

    /* ---- variant matching ---- */

    var idInput = root.querySelector('[data-pp-variant-id]');
    var optionSelects = root.querySelectorAll('[data-pp-option-index]');
    var variants = [];

    var dataEl = root.querySelector('[data-pp-variants]');
    if (dataEl) {
      try {
        variants = JSON.parse(dataEl.textContent) || [];
      } catch (e) {
        variants = [];
      }
    }

    var currentVariant = variants.filter(function (v) {
      return idInput && String(v.id) === String(idInput.value);
    })[0] || variants[0] || null;

    function findVariant() {
      if (!optionSelects.length) return currentVariant;

      var chosen = [];
      each(optionSelects, function (sel) {
        chosen[parseInt(sel.getAttribute('data-pp-option-index'), 10)] = sel.value;
      });

      return variants.filter(function (v) {
        return (v.options || []).every(function (opt, i) {
          return opt === chosen[i];
        });
      })[0] || null;
    }

    /* Price fields are formatted server-side per variant, so there is no
       money formatting to get wrong on the client. */
    function paint(variant) {
      each(document.querySelectorAll('[data-pp-bind]'), function (el) {
        var key = el.getAttribute('data-pp-bind');
        var value = variant && variant[key];
        if (value === undefined || value === null || value === '') return;
        el.textContent = value;
      });

      var hasCompare = !!(variant && variant.compare);
      each(document.querySelectorAll('[data-pp-when="compare"]'), function (el) {
        el.hidden = !hasCompare;
      });

      if (idInput && variant) idInput.value = variant.id;
    }

    /* ---- mix-and-match picker ---- */

    var chipWrap = root.querySelector('[data-pp-chips]');
    var chips = chipWrap ? chipWrap.querySelectorAll('[data-pp-chip]') : [];
    var hint = root.querySelector('[data-pp-chiphint]');
    var propInputs = root.querySelectorAll('[data-pp-prop]');
    var combinedInput = root.querySelector('[data-pp-prop-combined]');

    var maxPicks = parseInt(chipWrap && chipWrap.getAttribute('data-pp-max'), 10) || 0;
    var required = chipWrap ? chipWrap.getAttribute('data-pp-required') === 'true' : false;

    var hintDefault = hint ? hint.getAttribute('data-pp-hint-default') || hint.textContent : '';
    var hintFull = hint ? hint.getAttribute('data-pp-hint-full') || '' : '';
    var hintRemaining = hint ? hint.getAttribute('data-pp-hint-remaining') || '' : '';
    var hintAtLimit = hint ? hint.getAttribute('data-pp-hint-limit') || '' : '';

    function picked() {
      return Array.prototype.filter.call(chips, function (c) {
        return c.getAttribute('aria-pressed') === 'true';
      });
    }

    function writeProperties(list) {
      var names = list.map(function (c) {
        return c.getAttribute('data-pp-chip');
      });

      /* A disabled input is not submitted, which keeps unfilled slots out
         of the cart line entirely rather than adding blank properties. */
      each(propInputs, function (input, i) {
        input.value = names[i] || '';
        input.disabled = !names[i];
      });

      if (combinedInput) {
        combinedInput.value = names.join(', ');
        combinedInput.disabled = names.length === 0;
      }
    }

    function updateHint(count) {
      if (!hint) return;

      if (maxPicks && count >= maxPicks) {
        hint.textContent = hintFull || hintDefault;
      } else if (count > 0 && hintRemaining) {
        hint.textContent = hintRemaining.replace('[count]', String(maxPicks - count));
      } else {
        hint.textContent = hintDefault;
      }
    }

    function refresh() {
      var list = picked();
      var count = list.length;

      writeProperties(list);
      updateHint(count);

      /* Once the limit is hit, unselected chips read as locked. aria-disabled
         rather than disabled, so a click still lands and can explain why. */
      each(chips, function (c) {
        var on = c.getAttribute('aria-pressed') === 'true';
        var locked = !on && maxPicks > 0 && count >= maxPicks;
        c.setAttribute('aria-disabled', String(locked));
      });

      setButtonState(count);
    }

    function setButtonState(count) {
      if (!addBtn) return;

      var soldOut = !currentVariant || currentVariant.available === false;
      var needsPicks = required && maxPicks > 0 && count < maxPicks;
      var label;

      if (soldOut) {
        label = addBtn.getAttribute('data-label-soldout');
      } else if (needsPicks) {
        label = addBtn.getAttribute('data-label-choose');
      } else {
        label = addBtn.getAttribute('data-label-add');
      }

      addBtn.disabled = soldOut;
      addBtn.setAttribute('data-pp-incomplete', String(needsPicks));

      var target = addBtn.querySelector('[data-pp-add-label]') || addBtn;
      if (label) target.textContent = label;

      /* "Sold out — $508" reads badly, so drop the price off the label. */
      each(addBtn.querySelectorAll('[data-pp-price-suffix]'), function (el) {
        el.hidden = soldOut;
      });

      each(document.querySelectorAll('[data-pp-buy]'), function (b) {
        b.disabled = soldOut;
      });
    }

    each(chips, function (chip) {
      chip.addEventListener('click', function () {
        var on = chip.getAttribute('aria-pressed') === 'true';

        if (!on && maxPicks > 0 && picked().length >= maxPicks) {
          if (hint && hintAtLimit) hint.textContent = hintAtLimit;
          return;
        }

        chip.setAttribute('aria-pressed', String(!on));
        refresh();
      });
    });

    each(optionSelects, function (sel) {
      sel.addEventListener('change', function () {
        currentVariant = findVariant();
        paint(currentVariant);
        setButtonState(picked().length);
      });
    });

    paint(currentVariant);
    refresh();

    /* ---- submission ---- */

    var errorEl = root.querySelector('[data-pp-error]');

    function fail(message) {
      if (!errorEl) {
        window.alert(message);
        return;
      }
      errorEl.textContent = message;
    }

    function clearError() {
      if (errorEl) errorEl.textContent = '';
    }

    if (form) {
      form.addEventListener('submit', function (event) {
        clearError();

        if (required && maxPicks > 0 && picked().length < maxPicks) {
          event.preventDefault();
          fail(chipWrap.getAttribute('data-pp-error-text') || 'Please finish choosing your designs.');
          if (chipWrap) {
            chipWrap.scrollIntoView({ block: 'center', behavior: 'smooth' });
            var first = chipWrap.querySelector('[data-pp-chip]:not([aria-disabled="true"])');
            if (first) first.focus();
          }
          return;
        }

        /* Native submit posts to /cart/add and lets Avone's own cart
           handler intercept. AJAX mode is opt-in for themes that don't. */
        if (form.getAttribute('data-pp-ajax') !== 'true') return;

        event.preventDefault();
        if (addBtn) addBtn.disabled = true;

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        })
          .then(function (res) {
            return res.json().then(function (body) {
              if (!res.ok) throw new Error(body.description || body.message || 'Add to cart failed.');
              return body;
            });
          })
          .then(function () {
            /* Nudge whichever cart drawer the theme uses, then fall back
               to the cart page so the add is never silently lost. */
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:refresh', { bubbles: true })
            );
            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));

            if (form.getAttribute('data-pp-ajax-redirect') === 'true') {
              window.location.href =
                (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'cart';
            }
          })
          .catch(function (err) {
            fail(err.message || 'Add to cart failed.');
          })
          .then(function () {
            if (addBtn) addBtn.disabled = false;
            setButtonState(picked().length);
          });
      });
    }
  }

  /* ------------------------------------------------------------------ *
   * Buy buttons living outside the buy box (sticky bar, final CTA)
   * ------------------------------------------------------------------ */

  function initBuyButton(btn) {
    btn.addEventListener('click', function () {
      var addBtn = document.querySelector('[data-pp-add]');
      if (!addBtn) return;

      /* If designs still need picking, send them to the picker instead of
         bouncing them off a validation error. */
      if (addBtn.getAttribute('data-pp-incomplete') === 'true') {
        var chipWrap = document.querySelector('[data-pp-chips]');
        if (chipWrap) {
          chipWrap.scrollIntoView({ block: 'center', behavior: 'smooth' });
          var first = chipWrap.querySelector('[data-pp-chip]:not([aria-disabled="true"])');
          if (first) first.focus();
          return;
        }
      }

      addBtn.click();
    });
  }

  /* ------------------------------------------------------------------ *
   * Sticky bar
   * ------------------------------------------------------------------ */

  function initSticky(bar) {
    var threshold = parseInt(bar.getAttribute('data-pp-threshold'), 10);
    if (isNaN(threshold)) threshold = 700;

    var ticking = false;

    function update() {
      bar.classList.toggle('is-on', window.scrollY > threshold);
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function boot(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;

    each(context.querySelectorAll('[data-pp-gallery]'), function (el) {
      bindOnce(el, initGallery);
    });
    each(context.querySelectorAll('[data-pp-buybox]'), function (el) {
      bindOnce(el, initBuyBox);
    });
    each(context.querySelectorAll('[data-pp-sticky]'), function (el) {
      bindOnce(el, initSticky);
    });
    each(context.querySelectorAll('[data-pp-buy]'), function (el) {
      bindOnce(el, initBuyButton);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  /* Theme editor: sections are swapped in and out of the DOM live. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
